import { clsx } from "clsx";
import { format } from "date-fns";
import { ChevronDown, Pencil, Plus, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import type { StageTreeNode } from "@/features/stages/types.ts";
import { useDeleteScheduleNote } from "../../hooks/use-delete-schedule-note.ts";
import { useSaveScheduleNote } from "../../hooks/use-save-schedule-note.ts";
import type { Note } from "../../types.ts";
import { SEVERITIES, SEVERITY_LABELS } from "../../utils/severity.ts";
import { isOpen, openBySeverity } from "../../utils/summary.ts";
import { type NoteDraft, NoteForm } from "../note-form";
import { SeverityIcon } from "../severity-icon";

export interface NotesStageFilter {
	id: string;
	name: string;
	ids: Set<string>;
}

function formatWhen(iso: string): string {
	return format(new Date(iso), "dd/MM/yyyy HH:mm");
}

function NoteItem({
	note,
	canManage,
	busy,
	onEdit,
	onToggleResolved,
	onDelete,
}: {
	note: Note;
	canManage: boolean;
	busy: boolean;
	onEdit: () => void;
	onToggleResolved: () => void;
	onDelete: () => void;
}) {
	const open = isOpen(note);
	return (
		<li
			className={clsx("note-item", !open && "is-resolved")}
			data-severity={note.severity}
			data-testid="note-item"
		>
			<SeverityIcon severity={note.severity} labelled />
			<div className="note-item__content">
				<p className="note-item__body">{note.body}</p>
				<p className="note-item__meta">
					<span className="note-item__stage">{note.stageName ?? "Obra"}</span>
					{" · "}
					{note.createdByName}
					{" · "}
					{formatWhen(note.createdAt)}
					{note.resolvedAt && ` · Resolvida em ${formatWhen(note.resolvedAt)}`}
				</p>
				{canManage && (
					<div className="note-item__actions">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={busy}
							onClick={onEdit}
						>
							<Pencil />
							Editar
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							disabled={busy}
							onClick={onToggleResolved}
						>
							{open ? "Resolver" : "Reabrir"}
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="note-item__delete"
							disabled={busy}
							onClick={onDelete}
						>
							<Trash2 />
							Excluir
						</Button>
					</div>
				)}
			</div>
		</li>
	);
}

export function NotesPanel({
	open,
	onOpenChange,
	constructionId,
	notes,
	isPending,
	isError,
	stages,
	stageFilter,
	onClearFilter,
	canManage,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	constructionId: string;
	notes: Note[];
	isPending: boolean;
	isError: boolean;
	stages: StageTreeNode[];
	stageFilter: NotesStageFilter | null;
	onClearFilter: () => void;
	canManage: boolean;
}) {
	const [draft, setDraft] = useState<NoteDraft | null>(null);
	const [formOpen, setFormOpen] = useState(false);
	const [toDelete, setToDelete] = useState<Note | null>(null);
	const save = useSaveScheduleNote(constructionId);
	const remove = useDeleteScheduleNote(constructionId);

	const visible = useMemo(
		() =>
			stageFilter
				? notes.filter(
						(note) => !!note.stageId && stageFilter.ids.has(note.stageId),
					)
				: notes,
		[notes, stageFilter],
	);
	const counts = useMemo(() => openBySeverity(visible), [visible]);
	const openNotes = visible.filter(isOpen);
	const resolvedNotes = visible.filter((note) => !isOpen(note));
	const busy = save.isPending || remove.isPending;

	function openForm(next: NoteDraft) {
		setDraft(next);
		setFormOpen(true);
	}

	async function toggleResolved(note: Note) {
		const resolved = isOpen(note);
		try {
			await save.mutateAsync({
				kind: "update",
				noteId: note.id,
				input: { resolved },
			});
			toast.success(
				resolved ? "Observação resolvida." : "Observação reaberta.",
			);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Não foi possível atualizar.",
			);
		}
	}

	async function confirmDelete() {
		if (!toDelete) return;
		try {
			await remove.mutateAsync(toDelete.id);
			toast.success("Observação excluída.");
			setToDelete(null);
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Não foi possível excluir.",
			);
		}
	}

	function renderList() {
		if (isError) {
			return (
				<p className="notes-panel__state is-error">
					Não foi possível carregar os alertas e observações.
				</p>
			);
		}
		if (isPending) {
			return <p className="notes-panel__state">Carregando…</p>;
		}
		return (
			<>
				<section className="notes-panel__group" aria-label="Abertas">
					<h3 className="notes-panel__group-title">
						Abertas <span>{openNotes.length}</span>
					</h3>
					{openNotes.length === 0 ? (
						<p className="notes-panel__empty">Nenhuma observação aberta.</p>
					) : (
						<ul className="notes-panel__list">
							{openNotes.map((note) => (
								<NoteItem
									key={note.id}
									note={note}
									canManage={canManage}
									busy={busy}
									onEdit={() => openForm({ note })}
									onToggleResolved={() => toggleResolved(note)}
									onDelete={() => setToDelete(note)}
								/>
							))}
						</ul>
					)}
				</section>
				<Collapsible className="notes-panel__group">
					<CollapsibleTrigger className="notes-panel__group-trigger">
						<h3 className="notes-panel__group-title">
							Resolvidas <span>{resolvedNotes.length}</span>
						</h3>
						<ChevronDown />
					</CollapsibleTrigger>
					<CollapsibleContent>
						{resolvedNotes.length === 0 ? (
							<p className="notes-panel__empty">
								Nenhuma observação resolvida.
							</p>
						) : (
							<ul className="notes-panel__list">
								{resolvedNotes.map((note) => (
									<NoteItem
										key={note.id}
										note={note}
										canManage={canManage}
										busy={busy}
										onEdit={() => openForm({ note })}
										onToggleResolved={() => toggleResolved(note)}
										onDelete={() => setToDelete(note)}
									/>
								))}
							</ul>
						)}
					</CollapsibleContent>
				</Collapsible>
			</>
		);
	}

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="right" className="notes-panel">
					<SheetHeader>
						<SheetTitle>Alertas e observações</SheetTitle>
						<SheetDescription>
							{stageFilter
								? `Etapa: ${stageFilter.name}`
								: "Riscos, impedimentos e contexto do cronograma."}
						</SheetDescription>
						{stageFilter && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="notes-panel__clear"
								onClick={onClearFilter}
							>
								<X />
								Ver todas as etapas
							</Button>
						)}
					</SheetHeader>
					<ul
						className="notes-panel__counters"
						aria-label="Abertas por severidade"
					>
						{SEVERITIES.map((severity) => (
							<li
								key={severity}
								className="notes-panel__counter"
								data-severity={severity}
							>
								<SeverityIcon severity={severity} />
								{SEVERITY_LABELS[severity]} {counts[severity]}
							</li>
						))}
					</ul>
					{canManage && (
						<div className="notes-panel__actions">
							<Button
								type="button"
								size="sm"
								onClick={() => openForm({ stageId: stageFilter?.id })}
							>
								<Plus />
								Nova observação
							</Button>
						</div>
					)}
					<div className="notes-panel__body">{renderList()}</div>
				</SheetContent>
			</Sheet>

			{canManage && (
				<>
					<NoteForm
						open={formOpen}
						onOpenChange={setFormOpen}
						constructionId={constructionId}
						stages={stages}
						draft={draft}
					/>
					<ConfirmDialog
						open={!!toDelete}
						onOpenChange={(next) => {
							if (!next) setToDelete(null);
						}}
						title="Excluir observação"
						description="A observação será removida do cronograma. Esta ação não pode ser desfeita."
						confirmLabel="Excluir"
						destructive
						isPending={remove.isPending}
						onConfirm={confirmDelete}
					/>
				</>
			)}
		</>
	);
}
